import React, {useEffect, useState} from "react";
import {
    Box,
    Button, Checkbox,
    FormControlLabel,
    Grid,
    Input,
    Modal,
    Radio,
    RadioGroup,
    Typography,
} from "@mui/material";
import {useDispatch} from "react-redux";
import {
    getConversionUrlSettings,
    saveConversionUrlSettings,
} from "../../../../../../../store/actionCreators/flyerConversionUrlSettings";
import {useTypedSelector} from "../../../../../../../hooks/useTypeSelector";
import {makeStyles} from "@mui/styles";
import _ from "lodash";
import validator from "validator";
import {
    CheckoutUrlSettingStateDto,
    ConversionDto,
    ExampleCodeAndQtyDto,
    OptionalVarsDto,
} from "../../../../../../../dtos";
import {PageSpinner} from "../../../../../../com/pagesTab/pagesSpinner/spinner";
import {SimpleConfirmPopup} from "../../../../../../../com/simpleConfirmPopup/SimpleConfirmPopup";
import {ReactComponent as TrashIcon} from "../../../../../../../assets/editors/delete_icon_main.svg";
import {OptionalVar} from "./optionalVar";
import {OptionalVarString} from "./optionalVarString";
import {ClosePopupBtn} from "../../../../../../../com/closePopupBtn/closePopupBtn";
import AccountCheckoutSettingForPopup from "../accountCheckoutSettingsForPopup/accountCheckoutSettingForPopup";

const useStyles = makeStyles(() => ({
    checkoutSettingPopup: {
        width: `770px`,
        backgroundColor: `#ffffff`,
        height: `98vh`,
        display: `flex`,
        flexFlow: `column`,
        "& .MuiInputBase-input": {
            padding: `5px`,
            height: `20px`,
        },
    },
    checkoutSettingPopupHeader: {
        padding: `6px 20px`,
        background: `linear-gradient(89.41deg, #0198D2 0%, #3540A8 100%)`,
        "& .MuiButton-root": {
            "& > svg": {
                "& > path": {
                    fill: `#ffffff`,
                },
            },
        },
    },
    conversionUrlSettingInput: {
        width: `100%`,
        lineHeight: `1`,
        // backgroundColor: '#ffffff',
        border: `1px solid #888888`,
        borderRadius: "5px",
        "&.MuiInputBase-multiline": {
            padding: `0`,
        },
        "&.MuiInput-root": {
            fontSize: "14px",
        },
        "&.requiredField": {
            borderLeft: `4px solid #E5870E`,
            borderRadius: `0 5px 5px 0`,
        },
        "&.notValid": {
            borderLeftColor: `red`,
        },
        "&.Mui-disabled": {
            backgroundColor: `#dbdbdb`,
        },
    },
    buttonConversionUrlSettings: {
        width: `100%`,
        // background: `linear-gradient(135.47deg, #DD5A01 3.9%, #F0CA21 100%)`,
        "&.MuiButton-root": {
            padding: `10px 0`,
            backgroundColor: `#014780`,
            "&:hover": {
                backgroundColor: `#01478080`,
            }
        },
        "&.Mui-disabled": {
            opacity: `0.5`,
        },
        "& > .MuiTypography-root": {
            lineHeight: `15px`,
        },
    },
    radioBtnGroup: {
        justifyContent: `space-between`,
        "& > .MuiFormControlLabel-root": {
            margin: `0`,
            "& > .MuiRadio-root": {
                padding: `3px`,
            },
        },
    },
    conversionUrlExampleWrapper: {
        cursor: "pointer",
        minHeight: "72px",
        "& > .MuiTypography-root": {
            wordBreak: `break-all`,
            // color: theme.palette.primary.light,
            textDecoration: `underline`,
        },
    },
    rowBox: {
        paddingBottom: `10px`,
    },
    trashButtonWrapper: {
        display: `flex`,
        justifyContent: `flex-end`,
        alignItems: `center`,
        "& > .Mui-disabled": {
            opacity: `0.5`,
        },
    },
    tabButton: {
        '&.MuiButton-root': {
            height: `38px`,
            display: `flex`,
            alignItems: `center`,
            justifyContent: `center`,
            textTransform: `capitalize`,
            width: `100%`,
            color: `black`,
            borderRadius: `0`,
            border: "1px solid black",
            '&:hover': {
                backgroundColor: `#e4e4e480`,
            },
            '&.activeTab': {
                borderLeft: "none",
                borderRight: "none",
                borderBottom: "none",
                backgroundColor: `#ffffff`,
                '&:hover': {
                    backgroundColor: `#ffffff`
                },
            },
        },
    },
    resultBox: {
        border: "5px solid #014780",
        margin: "0 5px",
        padding: "10px"
    }
}));

interface Props {
    isShow: boolean;
    closePopup: VoidFunction;
    settingForAccount: boolean;
    setCheckoutUrlSetting?: (settings: ConversionDto) => void;
    setFirstExample?: (firstExample: ExampleCodeAndQtyDto) => void;
    setSecondExample?: (secondExample: ExampleCodeAndQtyDto) => void;
    checkoutUrlDataForFlyer?: ConversionDto;
    accountCheckoutSettings?: ConversionDto;
    activeTabInUrlSetting?: "appTab" | "pcTab"
}

export const CheckoutSettingPopup: React.FC<Props> = ({
                                                          isShow,
                                                          closePopup,
                                                          settingForAccount,
                                                          setCheckoutUrlSetting,
                                                          setFirstExample,
                                                          setSecondExample,
                                                          checkoutUrlDataForFlyer,
                                                          accountCheckoutSettings,
                                                          activeTabInUrlSetting = "checkoutTab"
                                                      }) => {
    const dispatch = useDispatch();
    const {conversionUrlSetting} = useTypedSelector((state) => state);
    const conversionUrlSettingData = conversionUrlSetting.data;
    const {flyerDetails} = useTypedSelector((state) => state);
    const [isLoading, setLoadingStatus] = useState<boolean>(false);
    const classes = useStyles();
    const [conversionUrlSettingState, setConversionUrlSettingState] =
        useState<CheckoutUrlSettingStateDto>({});
    const [
        defaultConversionUrlSettingState,
        setDefaultConversionUrlSettingState,
    ] = useState<CheckoutUrlSettingStateDto>({});
    const [isDomainChanged, changeDomain] = useState(false);
    const [isChangesNotSaved, changeStatusForNotSavedChanges] = useState(false);
    // const [hoverTabName, setHoverTabName] = useState<string>(``);
    const [activeTabName, setActiveTab] = useState<string>(activeTabInUrlSetting);

    useEffect(() => {
        return () => {
            setActiveTab("checkoutTab");
        };
    }, []);

    useEffect(() => {
        if (isShow && settingForAccount) {
            dispatch(getConversionUrlSettings());
        } else if (isShow && !settingForAccount && checkoutUrlDataForFlyer) {
            const dataForState = setStatesForPopups(checkoutUrlDataForFlyer);
            setConversionUrlSettingState(dataForState);
            setDefaultConversionUrlSettingState(dataForState);
        }
    }, [isShow]);

    useEffect(() => {
        setActiveTab(activeTabInUrlSetting);
    }, [activeTabInUrlSetting]);


    useEffect(() => {
        setLoadingStatus(conversionUrlSetting.loading);
        if (
            ((conversionUrlSetting.type === "GET_CONVERSION_URL_SUCCESS" &&
                    conversionUrlSettingData) ||
                (conversionUrlSetting.type === "POST_CONVERSION_URL_SUCCESS" &&
                    conversionUrlSettingData)) &&
            isShow
        ) {
            if ((conversionUrlSetting.type === "GET_CONVERSION_URL_SUCCESS" &&
                conversionUrlSettingData)) {
                const dataForState = setStatesForPopups(conversionUrlSettingData);
                setConversionUrlSettingState(dataForState);
                setDefaultConversionUrlSettingState(dataForState);
                return;
            }

            if (conversionUrlSetting.type === "POST_CONVERSION_URL_SUCCESS") {
                closePopup();
                // if (flyerDetails.flyRecId && settingForAccount) {
                //     dispatch(getFlippingFlyer(flyerDetails.flyRecId));
                // }
                return;
            }
        }
    }, [conversionUrlSetting]);

    const setStatesForPopups = (
        dataForState: ConversionDto
    ): CheckoutUrlSettingStateDto => {
        let data: CheckoutUrlSettingStateDto = {
            conversionEmail: dataForState?.conversionEmail || "",
            conversionType: dataForState?.conversionType || 0,
            conversionNotificationText: dataForState.conversionNotificationText || "",
            conversionPhoneNo: dataForState.conversionPhoneNo || "",
            conversionPhoneMessage: dataForState.conversionPhoneMessage || "",
            conversionUrlBase: dataForState?.conversionUrlBase || ``,
            conversionUrlBaseApp: dataForState?.conversionUrlBaseApp || ``,
            itemListVarName: dataForState?.itemListVarName || ``,
            itemListVarNameApp: dataForState?.itemListVarNameApp || ``,
            itemCodePrefix: dataForState?.itemCodePrefix || ``,
            itemCodePrefixApp: dataForState?.itemCodePrefixApp || ``,
            itemOptionalParamValue: dataForState?.itemOptionalParamValue || ``,
            itemOptionalParamValueApp: dataForState?.itemOptionalParamValueApp || ``,
            interItemSeparator: dataForState?.interItemSeparator || `,`,
            interItemSeparatorApp: dataForState?.interItemSeparatorApp || `,`,
            intraItemSeparator: dataForState?.intraItemSeparator || `,`,
            intraItemSeparatorApp: dataForState?.intraItemSeparatorApp || `,`,
            exampleItemCode1: `8690637919381`,
            exampleItemQTY1: `1`,
            exampleItemCode2: `7290112492500`,
            exampleItemQTY2: `1`,
            optionalVars: dataForState?.optionalVars?.length
                ? dataForState?.optionalVars
                : [],
            optionalVarsApp: dataForState?.optionalVarsApp?.length
                ? dataForState?.optionalVarsApp
                : [],
            isShowAppSetting: Boolean(dataForState?.conversionUrlBaseApp),
            // applyTo: localStorage.getItem('applyTo') && flyerDetails.flyRecId !== 0 ? localStorage.getItem('applyTo') : `toAccount`
        };
        let optionVarsStateWithEmptyRow: OptionalVarsDto[] = [];
        if (data.optionalVars && data.optionalVars.length) {
            optionVarsStateWithEmptyRow = [...data.optionalVars];
        }
        // added empty row for optionalVar
        if (optionVarsStateWithEmptyRow.length === 0) {
            optionVarsStateWithEmptyRow.push({
                name: "",
                value: "",
            });
        }
        data.optionalVars = optionVarsStateWithEmptyRow;

        let optionVarsStateWithEmptyRowApp: OptionalVarsDto[] = [];
        if (data.optionalVarsApp && data.optionalVarsApp.length) {
            optionVarsStateWithEmptyRowApp = [...data.optionalVarsApp];
        }
        // added empty row for optionalVar
        if (optionVarsStateWithEmptyRowApp.length === 0) {
            optionVarsStateWithEmptyRowApp.push({
                name: "",
                value: "",
            });
        }
        data.optionalVarsApp = optionVarsStateWithEmptyRowApp;
        return data;
    };

    const addRemoveRowFromOptionalVars = (
        isAddRow: boolean,
        rowNumber: number
    ) => {
        const settingObj = {...conversionUrlSettingState};
        if (isAddRow) {
            const currentOptionalVarRows = settingObj?.optionalVars?.map(
                (optionalVar) => optionalVar
            );
            currentOptionalVarRows?.push({
                name: "",
                value: "",
            });
            setConversionUrlSettingState({
                ...settingObj,
                optionalVars: [...currentOptionalVarRows!],
            });
        } else {
            const newStateForOptionalVarRows = settingObj.optionalVars?.filter(
                (optionalVar: OptionalVarsDto, index: number) => {
                    if (index + 1 !== rowNumber) {
                        return optionalVar;
                    }
                }
            );
            setConversionUrlSettingState({
                ...settingObj,
                optionalVars: [...newStateForOptionalVarRows!],
            });
        }
    };
    const addRemoveRowFromOptionalVarsApp = (
        isAddRow: boolean,
        rowNumber: number
    ) => {
        const settingObj = {...conversionUrlSettingState};
        if (isAddRow) {
            const currentOptionalVarRows = settingObj?.optionalVarsApp?.map(
                (optionalVar) => optionalVar
            );
            currentOptionalVarRows?.push({
                name: "",
                value: "",
            });
            setConversionUrlSettingState({
                ...settingObj,
                optionalVarsApp: [...currentOptionalVarRows!],
            });
        } else {
            const newStateForOptionalVarRows = settingObj.optionalVarsApp?.filter(
                (optionalVar: OptionalVarsDto, index: number) => {
                    if (index + 1 !== rowNumber) {
                        return optionalVar;
                    }
                }
            );
            setConversionUrlSettingState({
                ...settingObj,
                optionalVarsApp: [...newStateForOptionalVarRows!],
            });
        }
    };

    const handleChange = (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>,
        indexKey?: number
    ) => {
        const value = event.target.value;
        if (activeTabName === "appTab") {
            if (
                (event.target.name === "exampleItemQTY2" ||
                    event.target.name === "exampleItemQTY1") &&
                value !== ""
            ) {
                const isNumber = validator.isNumeric(value);
                if (isNumber && Number(value) <= 9999) {
                    setConversionUrlSettingState({
                        ...conversionUrlSettingState,
                        [event.target.name]: value.trim(),
                    });
                } else {
                    setConversionUrlSettingState({
                        ...conversionUrlSettingState,
                        [event.target.name]: 9999,
                    });
                }
            } else {
                if (event.target.name.indexOf("optionalVar") !== -1 && indexKey) {
                    setConversionUrlSettingState({
                        ...conversionUrlSettingState,
                        optionalVarsApp: conversionUrlSettingState.optionalVarsApp?.map(
                            (optionalVar: OptionalVarsDto, index: number) => {
                                let nameVal = optionalVar.name;
                                let valVal = optionalVar.value;
                                if (index + 1 === indexKey) {
                                    if (event.target.name.indexOf("optionalVarName") > -1) {
                                        nameVal = value.trim();
                                    } else {
                                        valVal = value.trim();
                                    }
                                }
                                return {
                                    name: nameVal,
                                    value: valVal,
                                };
                            }
                        ),
                    });
                } else {
                    setConversionUrlSettingState({
                        ...conversionUrlSettingState,
                        [event.target.name]: value.trim(),
                    });
                }
            }
        } else if (activeTabName === "checkoutTab") {
            if (event.target.name === "fulfilment_list_option") {
                setConversionUrlSettingState({
                    ...conversionUrlSettingState,
                    conversionType: Number(value),
                });
            } else {
                setConversionUrlSettingState({
                    ...conversionUrlSettingState,
                    [event.target.name]: value,
                });
            }
        } else {
            if (
                (event.target.name === "exampleItemQTY2" ||
                    event.target.name === "exampleItemQTY1") &&
                value !== ""
            ) {
                const isNumber = validator.isNumeric(value);
                if (isNumber && Number(value) <= 9999) {
                    setConversionUrlSettingState({
                        ...conversionUrlSettingState,
                        [event.target.name]: value.trim(),
                    });
                } else {
                    setConversionUrlSettingState({
                        ...conversionUrlSettingState,
                        [event.target.name]: 9999,
                    });
                }
            } else {
                if (event.target.name.indexOf("optionalVar") !== -1 && indexKey) {
                    setConversionUrlSettingState({
                        ...conversionUrlSettingState,
                        optionalVars: conversionUrlSettingState.optionalVars?.map(
                            (optionalVar: OptionalVarsDto, index: number) => {
                                let nameVal = optionalVar.name;
                                let valVal = optionalVar.value;
                                if (index + 1 === indexKey) {
                                    if (event.target.name.indexOf("optionalVarName") > -1) {
                                        nameVal = value.trim();
                                    } else {
                                        valVal = value.trim();
                                    }
                                }
                                return {
                                    name: nameVal,
                                    value: valVal,
                                };
                            }
                        ),
                    });
                } else {
                    setConversionUrlSettingState({
                        ...conversionUrlSettingState,
                        [event.target.name]: value.trim(),
                    });
                }
            }
        }
    };

    const handleChangeRadioBtn = (value: string) => {
        if (activeTabName === "checkoutTab") {
            setConversionUrlSettingState({
                ...conversionUrlSettingState,
                conversionType: Number(value),
            });
        }
    }

    const checkValidValue = (inputName: string): boolean => {

        let isValid = false;
        if (!_.isEmpty(conversionUrlSettingState)) {
            switch (inputName) {
                case `conversionUrlBase`:
                    isValid = conversionUrlSettingState![inputName]
                        ? validator.isURL(conversionUrlSettingState![inputName]!) &&
                        conversionUrlSettingState![inputName]!.toString().length <= 100
                        : false;
                    break;
                case `conversionEmail`:
                    isValid = (!settingForAccount || conversionUrlSettingState.conversionType !== 0) ? true : conversionUrlSettingState![inputName]
                        ? validator.isEmail(conversionUrlSettingState![inputName]!)
                        : false;
                    break;
                case `conversionPhoneNo`:
                    isValid = (!settingForAccount || conversionUrlSettingState.conversionType !== 3) ? true : conversionUrlSettingState![inputName]
                        ? validator.isMobilePhone(conversionUrlSettingState![inputName]!)
                        : false;
                    break;
                case `conversionPhoneMessage`:
                    isValid = (!settingForAccount || conversionUrlSettingState.conversionType !== 3) ? true : conversionUrlSettingState![inputName]
                        ? conversionUrlSettingState![inputName] !== ""
                        : false;
                    break;
                case `itemListVarName`:
                    isValid = conversionUrlSettingState[inputName]
                        ? conversionUrlSettingState[inputName]!.toString().length <= 40
                        : false;
                    break;
                case `conversionUrlBaseApp`:
                    isValid = conversionUrlSettingState.isShowAppSetting
                        ? conversionUrlSettingState![inputName]
                            // ? validator.isURL(conversionUrlSettingState![inputName]!) &&
                            ? conversionUrlSettingState![inputName]!.toString().length <= 100
                            : false
                        : true;
                    break;
                case `itemListVarNameApp`:
                    isValid = conversionUrlSettingState.isShowAppSetting
                        ? conversionUrlSettingState[inputName]
                            ? conversionUrlSettingState[inputName]!.toString().length <= 40
                            : false
                        : true;
                    break;
                default:
                    isValid = true;
                    break;
            }
        }
        return isValid;
    };

    const checkAllRequiredFields = (): boolean => {
        let isDisabled: boolean = false;
        if (
            JSON.stringify(defaultConversionUrlSettingState) ===
            JSON.stringify(conversionUrlSettingState)
        ) {
            isDisabled = true;
        } else {
            _.forOwn(conversionUrlSettingState, function (value, property) {
                const isValid = checkValidValue(property);
                if (!isValid) {
                    isDisabled = true;
                    return false;
                }
            });
        }

        return isDisabled;
    };

    const openUrlInTab = (e: React.MouseEvent<HTMLElement>) => {
        const target = e.currentTarget as Element;
        const urlWrapperChildrenArray = Array.from(target.childNodes);
        let url: string = ``;
        urlWrapperChildrenArray.forEach((selector) => {
            url += selector.textContent;
        });
        const encodeUrl = encodeURI(url);
        const isUrl = validator.isURL(encodeUrl);
        if (isUrl) {
            window.open(encodeUrl, "_blank")!.focus();
        }
    };

    const checkerBeforeSaveSettingsCheckoutUrl = () => {
        if (
            conversionUrlSettingData &&
            conversionUrlSettingState.conversionUrlBase !==
            conversionUrlSettingData.conversionUrlBase
        ) {
            changeDomain(true);
        } else {
            saveSettingUrl();
        }
    };

    const onCloseWithoutSaving = () => {
        closePopup();
    };

    const saveSettingUrl = () => {
        const optionalVarOnlyWithValue =
            conversionUrlSettingState.optionalVars?.filter(
                (optionalVar: OptionalVarsDto) => {
                    if (optionalVar.name !== "") {
                        return optionalVar;
                    }
                }
            );
        const optionalVarOnlyWithValueApp =
            conversionUrlSettingState.optionalVarsApp?.filter(
                (optionalVar: OptionalVarsDto) => {
                    if (optionalVar.name !== "") {
                        return optionalVar;
                    }
                }
            );

        const jsonForRequest: ConversionDto = {
            conversionUrlBase: conversionUrlSettingState.conversionUrlBase,
            conversionUrlBaseApp: conversionUrlSettingState.isShowAppSetting ? conversionUrlSettingState.conversionUrlBaseApp : null,
            itemListVarName: conversionUrlSettingState.itemListVarName,
            itemListVarNameApp: conversionUrlSettingState.isShowAppSetting ? conversionUrlSettingState.itemListVarNameApp : null,
            itemCodePrefix: conversionUrlSettingState.itemCodePrefix,
            itemCodePrefixApp: conversionUrlSettingState.isShowAppSetting ? conversionUrlSettingState.itemCodePrefixApp : null,
            interItemSeparator: conversionUrlSettingState.interItemSeparator,
            interItemSeparatorApp: conversionUrlSettingState.isShowAppSetting ? conversionUrlSettingState.interItemSeparatorApp : null,
            itemOptionalParamValue: conversionUrlSettingState.itemOptionalParamValue,
            itemOptionalParamValueApp: conversionUrlSettingState.isShowAppSetting ? conversionUrlSettingState.itemOptionalParamValueApp : null,
            intraItemSeparator: conversionUrlSettingState.intraItemSeparator,
            intraItemSeparatorApp: conversionUrlSettingState.isShowAppSetting ? conversionUrlSettingState.intraItemSeparatorApp : null,
            optionalVars: optionalVarOnlyWithValue,
            optionalVarsApp: conversionUrlSettingState.isShowAppSetting ? optionalVarOnlyWithValueApp : null,
            conversionType: settingForAccount ? conversionUrlSettingState.conversionType : conversionUrlSettingData?.conversionType,
            conversionEmail: settingForAccount ? conversionUrlSettingState.conversionEmail : conversionUrlSettingData?.conversionEmail,
            conversionPhoneNo: settingForAccount ? conversionUrlSettingState.conversionPhoneNo : conversionUrlSettingData?.conversionPhoneNo,
            conversionPhoneMessage: settingForAccount ? conversionUrlSettingState.conversionPhoneMessage : conversionUrlSettingData?.conversionPhoneMessage,
            // optionalVarName: conversionUrlSettingState.optionalVarName,
            // optionalVarValue: conversionUrlSettingState.optionalVarValue
        };
        if (flyerDetails.flyRecId && !settingForAccount && setCheckoutUrlSetting) {
            setCheckoutUrlSetting({
                ...jsonForRequest,
            });
            if (setFirstExample) {
                setFirstExample({
                    code: conversionUrlSettingState.exampleItemCode1
                        ? conversionUrlSettingState.exampleItemCode1
                        : ``,
                    qty: conversionUrlSettingState.exampleItemQTY1
                        ? conversionUrlSettingState.exampleItemQTY1
                        : ``,
                });
            }
            if (setSecondExample) {
                setSecondExample({
                    code: conversionUrlSettingState.exampleItemCode2
                        ? conversionUrlSettingState.exampleItemCode2
                        : ``,
                    qty: conversionUrlSettingState.exampleItemQTY2
                        ? conversionUrlSettingState.exampleItemQTY2
                        : ``,
                });
            }
            closePopup();
        } else {
            dispatch(
                saveConversionUrlSettings(
                    jsonForRequest,
                    settingForAccount,
                    flyerDetails.flyRecId
                )
            );
        }
    };

    const deleteCheckoutSetting = () => {
        let data: CheckoutUrlSettingStateDto;
        if (activeTabName === "pcTab") {
            data = {
                conversionUrlBase: ``,
                itemListVarName: ``,
                itemCodePrefix: ``,
                itemOptionalParamValue: ``,
                interItemSeparator: `,`,
                intraItemSeparator: `,`,
                exampleItemCode1: `8690637919381`,
                exampleItemQTY1: `1`,
                exampleItemCode2: `7290112492500`,
                exampleItemQTY2: `1`,
                optionalVars: [
                    {
                        name: "",
                        value: "",
                    },
                ],
                // applyTo: localStorage.getItem('applyTo') && flyerDetails.flyRecId !== 0 ? localStorage.getItem('applyTo') : `toAccount`
            };
        } else {
            data = {
                conversionUrlBaseApp: ``,
                itemListVarNameApp: ``,
                itemCodePrefixApp: ``,
                itemOptionalParamValueApp: ``,
                interItemSeparatorApp: `,`,
                intraItemSeparatorApp: `,`,
                exampleItemCode1: `8690637919381`,
                exampleItemQTY1: `1`,
                exampleItemCode2: `7290112492500`,
                exampleItemQTY2: `1`,
                optionalVarsApp: [
                    {
                        name: "",
                        value: "",
                    },
                ],
                // applyTo: localStorage.getItem('applyTo') && flyerDetails.flyRecId !== 0 ? localStorage.getItem('applyTo') : `toAccount`
            };
        }
        setConversionUrlSettingState({...conversionUrlSettingState, ...data});
        // const jsonForRequest: ConversionDto = {
        //     conversionType: conversionUrlSettingData?.conversionType,
        //     conversionEmail: conversionUrlSettingData?.conversionEmail
        // };
        // dispatch(saveConversionUrlSettings(jsonForRequest, settingForAccount, flyerDetails.flyRecId));
    };

    const replaceFlyerConversionUrl = () => {
        if (accountCheckoutSettings) {
            const dataForState = setStatesForPopups(accountCheckoutSettings);
            setConversionUrlSettingState(dataForState);
        }
    };

    const onClosePopup = () => {
        if (!checkAllRequiredFields()) {
            changeStatusForNotSavedChanges(true);
        } else {
            closePopup();
        }
    };

    const optionalVars = conversionUrlSettingState.optionalVars?.map(
        (variable: OptionalVarsDto, index: number) => {
            return (
                <OptionalVar
                    key={index}
                    variable={variable}
                    index={index + 1}
                    checkValidValue={checkValidValue}
                    handleChange={handleChange}
                    allVarsData={conversionUrlSettingState.optionalVars!}
                    addRemoveRowFromOptionalVars={addRemoveRowFromOptionalVars}
                />
            );
        }
    );
    const optionalVarsApp = conversionUrlSettingState.optionalVarsApp?.map(
        (variable: OptionalVarsDto, index: number) => {
            return (
                <OptionalVar
                    key={index}
                    variable={variable}
                    index={index + 1}
                    checkValidValue={checkValidValue}
                    handleChange={handleChange}
                    allVarsData={conversionUrlSettingState.optionalVarsApp!}
                    addRemoveRowFromOptionalVars={addRemoveRowFromOptionalVarsApp}
                />
            );
        }
    );

    const onClickOnTab = (event: React.MouseEvent<HTMLButtonElement>) => {
        const target = event.currentTarget as HTMLButtonElement;
        const name = target.name;
        if (name !== activeTabName) {
            setActiveTab(name);
        }
    }

    const onChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        setConversionUrlSettingState({
            ...conversionUrlSettingState,
            isShowAppSetting: isChecked
        });
    }

    const optionalVarStrings = conversionUrlSettingState.optionalVars?.map(
        (variable: OptionalVarsDto, index) => {
            return <OptionalVarString key={index} variable={variable}/>;
        }
    );
    const optionalVarStringsApp = conversionUrlSettingState.optionalVarsApp?.map(
        (variable: OptionalVarsDto, index) => {
            return <OptionalVarString key={index} variable={variable}/>;
        }
    );
    const onChangePhoneNumber = (phoneNumber: string) => {
        setConversionUrlSettingState({
            ...conversionUrlSettingState,
            conversionPhoneNo: phoneNumber,
        });
    };


    return (
        <>
            <Modal
                open={isShow}
                sx={{
                    display: `flex`,
                    justifyContent: `center`,
                    alignItems: `center`,
                }}
            >
                {isLoading ? (
                    <Box
                        sx={{
                            height: `100vh`,
                            width: `100vw`,
                            display: `flex`,
                            alignItems: `center`,
                            justifyContent: `center`,
                        }}
                    >
                        <PageSpinner/>
                    </Box>
                ) : (
                    <Box className={classes.checkoutSettingPopup}>
                        {/*header*/}
                        <Box
                            className={classes.checkoutSettingPopupHeader}
                        >
                            <Grid container alignItems={`center`}>
                                <Grid item xs={10}>
                                    <Typography color={`#ffffff`} fontWeight={700}>
                                        Checkout URL settings{" "}
                                        {settingForAccount ? `for Account` : `for Flyer`}
                                    </Typography>
                                </Grid>
                                <Grid item xs>
                                    <Box sx={{display: `flex`, justifyContent: `flex-end`}}>
                                        <ClosePopupBtn onClose={onClosePopup}/>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                        {/*note*/}
                        {
                            activeTabName !== "checkoutTab" &&
                            <Typography fontSize={`12px`} pt={`10px`} px={`20px`}>
                                This setting is designed for Account’s administrator.
                                <br/>
                                Enter the following parameters in order to create a working URL
                                that sends My List items and quantities to your online store.
                                <br/>
                                First see how the current color-coded default values form the
                                RESULT URL in the bottom of this screen.
                                <br/>
                                Then edit (or remove) the values as necessary in order to create
                                the RESULT URL that works for you.
                            </Typography>
                        }
                        {
                            activeTabName === "checkoutTab" &&
                            <Typography fontSize={`12px`} pt={`10px`} px={`20px`} sx={{
                                display: "flex",
                                alignItems: "center",
                                height: "82px"
                            }}>
                                This setting is designed for Account’s administrator.
                                <br/>
                                Enter the following parameters in order to create default values for checkout options
                                via email and WhatsApp.
                                <br/>
                                Then you can copy these values to a new flyer you create.
                            </Typography>
                        }
                        {/*content*/}
                        <Grid container sx={{
                            marginTop: "12px",
                            height: `38px`,
                            backgroundColor: `#C4C4C4`,
                        }} alignItems={"center"}>
                            {
                                settingForAccount &&
                                <Grid item xs>
                                    <Button
                                        disableTouchRipple
                                        disableRipple
                                        name={`checkoutTab`}
                                        onClick={onClickOnTab}
                                        className={`${classes.tabButton} ${activeTabName === `checkoutTab` ? `activeTab` : ``}`}
                                    >
                                        <Typography
                                            fontSize={`14px`}
                                            fontWeight={`900`}
                                        >
                                            Checkout
                                        </Typography>
                                    </Button>
                                </Grid>
                            }
                            <Grid item xs>
                                <Button
                                    disableTouchRipple
                                    disableRipple
                                    name={`pcTab`}
                                    onClick={onClickOnTab}
                                    className={`${classes.tabButton} ${activeTabName === `pcTab` ? `activeTab` : ``}`}
                                >
                                    <Typography
                                        fontSize={`14px`}
                                        fontWeight={`900`}
                                    >
                                        URL for PC (Default)
                                    </Typography>
                                </Button>
                            </Grid>
                            <Grid item xs>
                                <Button
                                    disableTouchRipple
                                    disableRipple
                                    name={`appTab`}
                                    onClick={onClickOnTab}
                                    className={`${classes.tabButton} ${activeTabName === `appTab` ? `activeTab` : ``}`}
                                >
                                    <Typography
                                        fontSize={`14px`}
                                        fontWeight={`900`}
                                    >
                                        URL for App
                                    </Typography>
                                </Button>
                            </Grid>
                        </Grid>
                        <Box px={`20px`} pt={`10px`}>
                            <Grid container alignItems={"center"}
                                  sx={{
                                      visibility: activeTabName === `appTab` ? "visible" : "hidden"
                                  }}
                            >
                                <Grid item xs={4}>
                                    <Typography fontWeight={700} variant={`body1`}>
                                        Use App Url
                                    </Typography>
                                    {/*<Typography*/}
                                    {/*    variant={`body2`}*/}
                                    {/*    display={"inline-block"}*/}
                                    {/*    color={`error.main`}*/}
                                    {/*>*/}
                                    {/*    **/}
                                    {/*</Typography>*/}
                                </Grid>
                                <Grid item xs={8}>
                                    <Box sx={{
                                        height: "100%",
                                        display: "flex",
                                        alignItems: "center"
                                    }}>
                                        <Checkbox
                                            sx={{
                                                padding: 0,
                                            }}
                                            name={"useAppUrl"}
                                            disableRipple disableTouchRipple
                                            inputProps={{'aria-label': 'controlled'}}
                                            size={'small'}
                                            checked={conversionUrlSettingState.isShowAppSetting}
                                            onChange={onChangeCheckbox}
                                        />

                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                        {
                            (activeTabName === `appTab` || activeTabName === `pcTab`) &&
                            <>
                                <Box
                                    sx={{
                                        flex: `1 1`,
                                        overflow: `auto`,
                                        visibility: activeTabName === "pcTab" || (conversionUrlSettingState.isShowAppSetting && activeTabName === `appTab`) ? "visible" : "hidden"
                                    }}
                                >
                                    <Box
                                        px={`20px`} py={`10px`}
                                        sx={{
                                            flex: `1 1`,
                                            overflow: `auto`,
                                        }}
                                    >
                                        {/*columns*/}
                                        <Box className={classes.rowBox}>
                                            <Grid container alignItems={`center`}>
                                                <Grid item xs={4}>
                                                    <Typography fontWeight={700} variant={`body1`}>
                                                        Parameter
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs>
                                                    <Typography fontWeight={700} variant={`body1`}>
                                                        Value
                                                    </Typography>
                                                </Grid>
                                                {!settingForAccount && (
                                                    <Grid item xs={4}>
                                                        {/*<Typography fontWeight={700} variant={`body1`}>*/}
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "flex-end"
                                                            }}
                                                        >
                                                            <Typography
                                                                onClick={replaceFlyerConversionUrl}
                                                                variant={`body1`}
                                                                sx={{cursor: `pointer`, textDecoration: "underline"}}
                                                            >
                                                                Get Account Checkout URL
                                                            </Typography>
                                                            {/*<Button onClick={replaceFlyerConversionUrl}*/}
                                                            {/*        className={classes.buttonConversionUrlSettings}*/}
                                                            {/*        // disabled={!Boolean(conversionUrlSettingState?.conversionUrlBase)}*/}
                                                            {/*>*/}
                                                            {/*    <Typography textTransform={`inherit`} color={`#ffffff`}>*/}
                                                            {/*        Get Account Checkout URL*/}
                                                            {/*    </Typography>*/}
                                                            {/*</Button>*/}
                                                        </Box>
                                                        {/*</Typography>*/}
                                                    </Grid>
                                                )}
                                                {/*{settingForAccount && (*/}
                                                <Grid item xs={1}>
                                                    {/*<Typography fontWeight={700} variant={`body1`}>*/}
                                                    <Box className={classes.trashButtonWrapper}>
                                                        <Button
                                                            onClick={deleteCheckoutSetting}
                                                            disabled={(activeTabName === "pcTab" && !Boolean(conversionUrlSettingState?.conversionUrlBase)) || (activeTabName === "appTab" && !Boolean(conversionUrlSettingState?.conversionUrlBaseApp))}
                                                        >
                                                            <TrashIcon/>
                                                        </Button>
                                                    </Box>
                                                    {/*</Typography>*/}
                                                </Grid>
                                                {/*)}*/}
                                            </Grid>
                                        </Box>
                                        {/*main fields*/}
                                        <Box className={classes.rowBox}>
                                            <Grid container>
                                                <Grid item xs={4}>
                                                    <Typography variant={`body2`} display={"inline-block"}>
                                                        Web shop Domain URL Header
                                                    </Typography>
                                                    {/*<Typography*/}
                                                    {/*    variant={`body2`}*/}
                                                    {/*    display={"inline-block"}*/}
                                                    {/*    color={`error.main`}*/}
                                                    {/*>*/}
                                                    {/*    **/}
                                                    {/*</Typography>*/}
                                                </Grid>
                                                <Grid item xs={8}>
                                                    <Input
                                                        disableUnderline
                                                        className={`${classes.conversionUrlSettingInput} ${
                                                            checkValidValue(activeTabName === "appTab" ? "conversionUrlBaseApp" : "conversionUrlBase") ? `` : `notValid`
                                                        } requiredField`}
                                                        name={activeTabName === `appTab` ? "conversionUrlBaseApp" : `conversionUrlBase`}
                                                        value={activeTabName === "appTab" ? conversionUrlSettingState.conversionUrlBaseApp : conversionUrlSettingState.conversionUrlBase}
                                                        onChange={handleChange}
                                                        inputProps={{maxLength: 100}}
                                                        rows={2}
                                                        multiline
                                                        placeholder={`https://company.com`}
                                                        sx={{backgroundColor: `#faffdb`}}
                                                        // variant="outlined"
                                                        // label="Outlined"
                                                        // size="small"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        <Box className={classes.rowBox}>
                                            <Grid container alignItems={`center`}>
                                                <Grid item xs={4}>
                                                    <Typography variant={`body2`} display={"inline-block"}>
                                                        Item List Variable
                                                    </Typography>
                                                    {/*<Typography*/}
                                                    {/*    variant={`body2`}*/}
                                                    {/*    display={"inline-block"}*/}
                                                    {/*    color={`error.main`}*/}
                                                    {/*>*/}
                                                    {/*    **/}
                                                    {/*</Typography>*/}
                                                </Grid>
                                                <Grid item xs={8}>
                                                    <Input
                                                        disableUnderline
                                                        className={`
                                            ${classes.conversionUrlSettingInput} 
                                            ${checkValidValue(activeTabName === "appTab" ? "itemListVarNameApp" : `itemListVarName`) ? `` : `notValid`}
                                            requiredField
                                            `}
                                                        name={activeTabName === "appTab" ? "itemListVarNameApp" : `itemListVarName`}
                                                        value={activeTabName === "appTab" ? conversionUrlSettingState.itemListVarNameApp : conversionUrlSettingState.itemListVarName}
                                                        onChange={handleChange}
                                                        inputProps={{maxLength: 40}}
                                                        sx={{backgroundColor: `#FFDBDB`}}
                                                        placeholder={`externalCart`}

                                                        // variant="outlined"
                                                        // label="Outlined"
                                                        // size="small"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        {/*section*/}
                                        <Box className={classes.rowBox}>
                                            <Grid container>
                                                <Grid item xs={4}>
                                                    <Typography fontWeight={700} variant={`body2`}>
                                                        Repeat item data
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        {/*additional fields*/}
                                        <Box className={classes.rowBox}>
                                            <Grid container alignItems={`center`}>
                                                <Grid item xs={4} pl={`2em`}>
                                                    <Typography variant={`body2`}>Item Code Prefix</Typography>
                                                </Grid>
                                                <Grid item xs={8}>
                                                    <Input
                                                        disableUnderline
                                                        className={`
                                        ${classes.conversionUrlSettingInput} 
                                        ${
                                                            checkValidValue(activeTabName === "appTab" ? "itemCodePrefixApp" : `itemCodePrefix`)
                                                                ? ``
                                                                : `notValid`
                                                        }
                                        `}
                                                        name={activeTabName === "appTab" ? "itemCodePrefixApp" : `itemCodePrefix`}
                                                        value={activeTabName === "appTab" ? conversionUrlSettingState.itemCodePrefixApp : conversionUrlSettingState.itemCodePrefix}
                                                        onChange={handleChange}
                                                        inputProps={{maxLength: 10}}
                                                        placeholder={`item_prefix`}
                                                        // variant="outlined"
                                                        // label="Outlined"
                                                        // size="small"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        <Box className={classes.rowBox}>
                                            <Grid container alignItems={`center`}>
                                                <Grid item xs={4} pl={`2em`}>
                                                    <Typography variant={`body2`} display={"inline-block"}>
                                                        Intra-item separator
                                                    </Typography>
                                                    {/*<Typography*/}
                                                    {/*    variant={`body2`}*/}
                                                    {/*    display={"inline-block"}*/}
                                                    {/*    color={`error.main`}*/}
                                                    {/*>*/}
                                                    {/*    **/}
                                                    {/*</Typography>*/}
                                                </Grid>
                                                <Grid item xs={8}>
                                                    <Box>
                                                        <RadioGroup
                                                            row
                                                            value={activeTabName === "appTab" ? conversionUrlSettingState.intraItemSeparatorApp : conversionUrlSettingState.intraItemSeparator}
                                                            onChange={(event) => {
                                                                handleChange(event);
                                                            }}
                                                            className={classes.radioBtnGroup}
                                                            name={activeTabName === "appTab" ? "intraItemSeparatorApp" : `intraItemSeparator`}
                                                        >
                                                            <FormControlLabel
                                                                value=","
                                                                control={<Radio size="small"/>}
                                                                label=","
                                                                sx={{width: `25%`}}
                                                            />
                                                            <FormControlLabel
                                                                value="."
                                                                control={<Radio size="small"/>}
                                                                label="."
                                                                sx={{width: `25%`}}
                                                            />
                                                            <FormControlLabel
                                                                value=":"
                                                                control={<Radio size="small"/>}
                                                                label=":"
                                                                sx={{width: `25%`}}
                                                            />
                                                            <FormControlLabel
                                                                value=";"
                                                                control={<Radio size="small"/>}
                                                                label=";"
                                                                sx={{width: `25%`}}
                                                            />
                                                        </RadioGroup>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        <Box className={classes.rowBox}>
                                            <Grid container alignItems={`center`}>
                                                <Grid item xs={4} pl={`2em`}>
                                                    <Typography variant={`body2`}>
                                                        Item Optional Parameter
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={8}>
                                                    <Input
                                                        disableUnderline
                                                        className={`
                                            ${classes.conversionUrlSettingInput} 
                                            ${checkValidValue(activeTabName === "appTab" ? "itemOptionalParamValueApp" : `itemOptionalParamValue`) ? `` : `notValid`}
                                            `}
                                                        name={activeTabName === "appTab" ? "itemOptionalParamValueApp" : `itemOptionalParamValue`}
                                                        value={activeTabName === "appTab" ? conversionUrlSettingState.itemOptionalParamValueApp : conversionUrlSettingState.itemOptionalParamValue}
                                                        onChange={handleChange}
                                                        inputProps={{maxLength: 40}}
                                                        placeholder={`optional`}
                                                        // variant="outlined"
                                                        // label="Outlined"
                                                        // size="small"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        <Box className={classes.rowBox}>
                                            <Grid container alignItems={`center`}>
                                                <Grid item xs={4} pl={`2em`}>
                                                    <Typography variant={`body2`} display={"inline-block"}>
                                                        Inter-item separator
                                                    </Typography>
                                                    {/*<Typography*/}
                                                    {/*    variant={`body2`}*/}
                                                    {/*    display={"inline-block"}*/}
                                                    {/*    color={`error.main`}*/}
                                                    {/*>*/}
                                                    {/*    **/}
                                                    {/*</Typography>*/}
                                                </Grid>
                                                <Grid item xs={8}>
                                                    <Box>
                                                        <RadioGroup
                                                            row
                                                            value={activeTabName === "appTab" ? conversionUrlSettingState.interItemSeparatorApp : conversionUrlSettingState.interItemSeparator}
                                                            onChange={(event) => {
                                                                handleChange(event);
                                                            }}
                                                            className={classes.radioBtnGroup}
                                                            name={activeTabName === "appTab" ? "interItemSeparatorApp" : `interItemSeparator`}
                                                        >
                                                            <FormControlLabel
                                                                value=","
                                                                control={<Radio size="small"/>}
                                                                label=","
                                                                sx={{width: `25%`}}
                                                            />
                                                            <FormControlLabel
                                                                value="."
                                                                control={<Radio size="small"/>}
                                                                label="."
                                                                sx={{width: `25%`}}
                                                            />
                                                            <FormControlLabel
                                                                value=":"
                                                                control={<Radio size="small"/>}
                                                                label=":"
                                                                sx={{width: `25%`}}
                                                            />
                                                            <FormControlLabel
                                                                value=";"
                                                                control={<Radio size="small"/>}
                                                                label=";"
                                                                sx={{width: `25%`}}
                                                            />
                                                        </RadioGroup>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        {/*main fields*/}
                                        {activeTabName === "pcTab" ? optionalVars : optionalVarsApp}
                                        {/*section*/}
                                        <Box className={classes.rowBox}>
                                            <Grid container>
                                                <Grid item xs={4}>
                                                    <Typography fontWeight={700} variant={`body2`}>
                                                        Example items data for URL
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        {/*additional fields*/}
                                        <Box className={classes.rowBox}>
                                            <Grid container alignItems={`center`}>
                                                <Grid item xs={4} pl={`2em`}>
                                                    <Typography variant={`body2`} display={"inline-block"}>
                                                        Example Item Code1
                                                    </Typography>
                                                    {/*<Typography*/}
                                                    {/*    variant={`body2`}*/}
                                                    {/*    display={"inline-block"}*/}
                                                    {/*    title={`Example Item Code`}*/}
                                                    {/*>*/}
                                                    {/*    **/}
                                                    {/*</Typography>*/}
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Input
                                                        disableUnderline
                                                        className={`
                                            ${classes.conversionUrlSettingInput} 
                                            ${checkValidValue(`exampleItemCode1`) ? `` : `notValid`}
                                            `}
                                                        name={`exampleItemCode1`}
                                                        value={conversionUrlSettingState.exampleItemCode1}
                                                        onChange={handleChange}
                                                        inputProps={{maxLength: 20}}
                                                        sx={{backgroundColor: `#dbffe7`}}
                                                        placeholder={`8001090678256`}
                                                        // variant="outlined"
                                                        // label="Outlined"
                                                        // size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={1} pl={`2em`}>
                                                    <Box
                                                        sx={{
                                                            display: `flex`,
                                                            justifyContent: `flex-end`,
                                                            pr: `5px`,
                                                        }}
                                                    >
                                                        <Typography variant={`body2`} display={"inline-block"}>
                                                            QTY1
                                                        </Typography>
                                                        {/*<Typography*/}
                                                        {/*    variant={`body2`}*/}
                                                        {/*    display={"inline-block"}*/}
                                                        {/*    title={`Example Item Quantity`}*/}
                                                        {/*>*/}
                                                        {/*    **/}
                                                        {/*</Typography>*/}
                                                        <Typography variant={`body2`} display={"inline-block"}>
                                                            :
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs>
                                                    <Input
                                                        disableUnderline
                                                        className={`
                                                ${
                                                            classes.conversionUrlSettingInput
                                                        } 
                                                ${
                                                            checkValidValue(
                                                                `exampleItemQTY1`
                                                            )
                                                                ? ``
                                                                : `notValid`
                                                        }
                                            `}
                                                        name={`exampleItemQTY1`}
                                                        value={conversionUrlSettingState.exampleItemQTY1}
                                                        onChange={handleChange}
                                                        inputProps={{maxLength: 40}}
                                                        placeholder={`1`}
                                                        // variant="outlined"
                                                        // label="Outlined"
                                                        // size="small"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        <Box className={classes.rowBox}>
                                            <Grid container alignItems={`center`}>
                                                <Grid item xs={4} pl={`2em`}>
                                                    <Typography variant={`body2`}>
                                                        Example Item Code2
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Input
                                                        disableUnderline
                                                        className={`
                                                ${
                                                            classes.conversionUrlSettingInput
                                                        } 
                                                ${
                                                            checkValidValue(
                                                                `exampleItemCode2`
                                                            )
                                                                ? ``
                                                                : `notValid`
                                                        }
                                            `}
                                                        name={`exampleItemCode2`}
                                                        value={conversionUrlSettingState.exampleItemCode2}
                                                        onChange={handleChange}
                                                        inputProps={{maxLength: 20}}
                                                        sx={{backgroundColor: `#dbffe7`}}
                                                        placeholder={`8001090678257`}
                                                        // variant="outlined"
                                                        // label="Outlined"
                                                        // size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={1} pl={`2em`}>
                                                    <Box
                                                        sx={{
                                                            display: `flex`,
                                                            justifyContent: `flex-end`,
                                                            pr: `5px`,
                                                        }}
                                                    >
                                                        <Typography variant={`body2`}>QTY2:</Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs>
                                                    <Input
                                                        disableUnderline
                                                        className={`
                                        ${classes.conversionUrlSettingInput} 
                                        ${
                                                            checkValidValue(`exampleItemQTY2`)
                                                                ? ``
                                                                : `notValid`
                                                        }
                                        `}
                                                        name={`exampleItemQTY2`}
                                                        value={conversionUrlSettingState.exampleItemQTY2}
                                                        onChange={handleChange}
                                                        placeholder={`2`}
                                                        // variant="outlined"
                                                        // label="Outlined"
                                                        // size="small"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Box>
                                    <Box
                                        className={`${classes.resultBox}`}
                                    >
                                        {/*section*/}
                                        <Box className={classes.rowBox}>
                                            <Grid container>
                                                <Grid item xs={4}>
                                                    <Typography fontWeight={700} variant={`body2`}>
                                                        Result URL
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        {/*result fields*/}
                                        <Box className={`${classes.rowBox}`}>
                                            <Grid container alignItems={`center`}>
                                                <Grid item xs={4}>
                                                    <Box>
                                                        <Typography variant={`body2`}>
                                                            Text updated from above
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{display: `flex`}}>
                                                        <Typography
                                                            variant={`body2`}
                                                            bgcolor={`success.light`}
                                                            sx={{width: `auto`}}
                                                        >
                                                            Text added by the System
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={8}>
                                                    {
                                                        activeTabName === "pcTab" &&
                                                        <Box
                                                            className={classes.conversionUrlExampleWrapper}
                                                            onClick={openUrlInTab}
                                                        >
                                                            {conversionUrlSettingState.conversionUrlBase !== "" && (
                                                                <Typography
                                                                    variant={`body2`}
                                                                    bgcolor={`#faffdb`}
                                                                    display={`inline`}
                                                                >
                                                                    {conversionUrlSettingState.conversionUrlBase}
                                                                </Typography>
                                                            )}
                                                            {conversionUrlSettingState.conversionUrlBase !== "" &&
                                                                conversionUrlSettingState.itemListVarName !== "" && (
                                                                    <Typography
                                                                        variant={`body2`}
                                                                        bgcolor={`success.light`}
                                                                        display={`inline`}
                                                                    >
                                                                        ?
                                                                    </Typography>
                                                                )}
                                                            {conversionUrlSettingState.itemListVarName !== "" && (
                                                                <Typography
                                                                    variant={`body2`}
                                                                    bgcolor={`#FFDBDB`}
                                                                    display={`inline`}
                                                                >
                                                                    {conversionUrlSettingState.itemListVarName}
                                                                </Typography>
                                                            )}
                                                            {(conversionUrlSettingState.exampleItemCode1 !== "" ||
                                                                conversionUrlSettingState.exampleItemCode2 !== "") && (
                                                                <Typography
                                                                    variant={`body2`}
                                                                    bgcolor={`success.light`}
                                                                    display={`inline`}
                                                                >
                                                                    =
                                                                </Typography>
                                                            )}
                                                            {conversionUrlSettingState.itemCodePrefix !== "" &&
                                                                conversionUrlSettingState.exampleItemCode1 !== "" &&
                                                                conversionUrlSettingState.exampleItemQTY1 !== "" && (
                                                                    <Typography variant={`body2`} display={`inline`}>
                                                                        {conversionUrlSettingState.itemCodePrefix}
                                                                    </Typography>
                                                                )}
                                                            {conversionUrlSettingState.exampleItemCode1 !== "" &&
                                                                conversionUrlSettingState.exampleItemQTY1 !== "" && (
                                                                    <>
                                                                        <Typography
                                                                            variant={`body2`}
                                                                            bgcolor={`#dbffe7`}
                                                                            display={`inline`}
                                                                        >
                                                                            {conversionUrlSettingState.exampleItemCode1}
                                                                        </Typography>
                                                                        <Typography variant={`body2`}
                                                                                    display={`inline`}>
                                                                            {conversionUrlSettingState.intraItemSeparator}
                                                                        </Typography>
                                                                        <Typography variant={`body2`}
                                                                                    display={`inline`}>
                                                                            {conversionUrlSettingState.exampleItemQTY1}
                                                                        </Typography>
                                                                    </>
                                                                )}
                                                            {conversionUrlSettingState.itemOptionalParamValue !==
                                                                "" &&
                                                                conversionUrlSettingState.exampleItemCode1 !== "" &&
                                                                conversionUrlSettingState.exampleItemQTY1 !== "" && (
                                                                    <>
                                                                        <Typography variant={`body2`}
                                                                                    display={`inline`}>
                                                                            {conversionUrlSettingState.intraItemSeparator}
                                                                        </Typography>
                                                                        <Typography variant={`body2`}
                                                                                    display={`inline`}>
                                                                            {conversionUrlSettingState.itemOptionalParamValue}
                                                                        </Typography>
                                                                    </>
                                                                )}
                                                            {((conversionUrlSettingState.exampleItemCode1 !== "" &&
                                                                    conversionUrlSettingState.exampleItemQTY1 !== "" &&
                                                                    conversionUrlSettingState.exampleItemCode2 !== "" &&
                                                                    conversionUrlSettingState.exampleItemQTY2 !== "") ||
                                                                (conversionUrlSettingState.exampleItemCode1 !== "" &&
                                                                    conversionUrlSettingState.exampleItemQTY1 !== "" &&
                                                                    conversionUrlSettingState.optionalVarName !==
                                                                    "")) && (
                                                                <Typography variant={`body2`} display={`inline`}>
                                                                    {conversionUrlSettingState.interItemSeparator}
                                                                </Typography>
                                                            )}
                                                            {conversionUrlSettingState.itemCodePrefix !== "" &&
                                                                conversionUrlSettingState.exampleItemCode2 !== "" &&
                                                                conversionUrlSettingState.exampleItemQTY2 !== "" && (
                                                                    <Typography variant={`body2`} display={`inline`}>
                                                                        {conversionUrlSettingState.itemCodePrefix}
                                                                    </Typography>
                                                                )}
                                                            {conversionUrlSettingState.exampleItemCode2 !== "" &&
                                                                conversionUrlSettingState.exampleItemQTY2 !== "" && (
                                                                    <>
                                                                        <Typography
                                                                            variant={`body2`}
                                                                            bgcolor={`#dbffe7`}
                                                                            display={`inline`}
                                                                        >
                                                                            {conversionUrlSettingState.exampleItemCode2}
                                                                        </Typography>
                                                                        <Typography variant={`body2`}
                                                                                    display={`inline`}>
                                                                            {conversionUrlSettingState.intraItemSeparator}
                                                                        </Typography>
                                                                        <Typography variant={`body2`}
                                                                                    display={`inline`}>
                                                                            {conversionUrlSettingState.exampleItemQTY2}
                                                                        </Typography>
                                                                    </>
                                                                )}
                                                            {conversionUrlSettingState.itemOptionalParamValue !==
                                                                "" &&
                                                                conversionUrlSettingState.exampleItemCode2 !== "" &&
                                                                conversionUrlSettingState.exampleItemQTY2 !== "" && (
                                                                    <>
                                                                        <Typography variant={`body2`}
                                                                                    display={`inline`}>
                                                                            {conversionUrlSettingState.intraItemSeparator}
                                                                        </Typography>
                                                                        <Typography variant={`body2`}
                                                                                    display={`inline`}>
                                                                            {conversionUrlSettingState.itemOptionalParamValue}
                                                                        </Typography>
                                                                    </>
                                                                )}
                                                            {/*{*/}
                                                            {/*    conversionUrlSettingState.exampleItemCode2 !== '' &&*/}
                                                            {/*    conversionUrlSettingState.exampleItemQTY2 !== '' &&*/}
                                                            {/*    conversionUrlSettingState.optionalVarName !== '' &&*/}
                                                            {/*    <Typography variant={`body2`} display={`inline`}>*/}
                                                            {/*        {conversionUrlSettingState.interItemSeparator}*/}
                                                            {/*    </Typography>*/}
                                                            {/*}*/}

                                                            {optionalVarStrings}
                                                        </Box>
                                                    }
                                                    {
                                                        activeTabName === "appTab" &&
                                                        <Box
                                                            className={classes.conversionUrlExampleWrapper}
                                                            onClick={openUrlInTab}
                                                        >
                                                            {conversionUrlSettingState.conversionUrlBaseApp !== "" && (
                                                                <Typography
                                                                    variant={`body2`}
                                                                    bgcolor={`#faffdb`}
                                                                    display={`inline`}
                                                                >
                                                                    {conversionUrlSettingState.conversionUrlBaseApp}
                                                                </Typography>
                                                            )}
                                                            {conversionUrlSettingState.conversionUrlBaseApp !== "" &&
                                                                conversionUrlSettingState.itemListVarNameApp !== "" && (
                                                                    <Typography
                                                                        variant={`body2`}
                                                                        bgcolor={`success.light`}
                                                                        display={`inline`}
                                                                    >
                                                                        ?
                                                                    </Typography>
                                                                )}
                                                            {conversionUrlSettingState.itemListVarNameApp !== "" && (
                                                                <Typography
                                                                    variant={`body2`}
                                                                    bgcolor={`#FFDBDB`}
                                                                    display={`inline`}
                                                                >
                                                                    {conversionUrlSettingState.itemListVarNameApp}
                                                                </Typography>
                                                            )}
                                                            {(conversionUrlSettingState.exampleItemCode1 !== "" ||
                                                                conversionUrlSettingState.exampleItemCode2 !== "") && (
                                                                <Typography
                                                                    variant={`body2`}
                                                                    bgcolor={`success.light`}
                                                                    display={`inline`}
                                                                >
                                                                    =
                                                                </Typography>
                                                            )}
                                                            {conversionUrlSettingState.itemCodePrefixApp !== "" &&
                                                                conversionUrlSettingState.exampleItemCode1 !== "" &&
                                                                conversionUrlSettingState.exampleItemQTY1 !== "" && (
                                                                    <Typography variant={`body2`} display={`inline`}>
                                                                        {conversionUrlSettingState.itemCodePrefixApp}
                                                                    </Typography>
                                                                )}
                                                            {conversionUrlSettingState.exampleItemCode1 !== "" &&
                                                                conversionUrlSettingState.exampleItemQTY1 !== "" && (
                                                                    <>
                                                                        <Typography
                                                                            variant={`body2`}
                                                                            bgcolor={`#dbffe7`}
                                                                            display={`inline`}
                                                                        >
                                                                            {conversionUrlSettingState.exampleItemCode1}
                                                                        </Typography>
                                                                        <Typography variant={`body2`}
                                                                                    display={`inline`}>
                                                                            {conversionUrlSettingState.intraItemSeparatorApp}
                                                                        </Typography>
                                                                        <Typography variant={`body2`}
                                                                                    display={`inline`}>
                                                                            {conversionUrlSettingState.exampleItemQTY1}
                                                                        </Typography>
                                                                    </>
                                                                )}
                                                            {conversionUrlSettingState.itemOptionalParamValueApp !==
                                                                "" &&
                                                                conversionUrlSettingState.exampleItemCode1 !== "" &&
                                                                conversionUrlSettingState.exampleItemQTY1 !== "" && (
                                                                    <>
                                                                        <Typography variant={`body2`}
                                                                                    display={`inline`}>
                                                                            {conversionUrlSettingState.intraItemSeparatorApp}
                                                                        </Typography>
                                                                        <Typography variant={`body2`}
                                                                                    display={`inline`}>
                                                                            {conversionUrlSettingState.itemOptionalParamValueApp}
                                                                        </Typography>
                                                                    </>
                                                                )}
                                                            {((conversionUrlSettingState.exampleItemCode1 !== "" &&
                                                                    conversionUrlSettingState.exampleItemQTY1 !== "" &&
                                                                    conversionUrlSettingState.exampleItemCode2 !== "" &&
                                                                    conversionUrlSettingState.exampleItemQTY2 !== "") ||
                                                                (conversionUrlSettingState.exampleItemCode1 !== "" &&
                                                                    conversionUrlSettingState.exampleItemQTY1 !== "" &&
                                                                    conversionUrlSettingState.optionalVarName !==
                                                                    "")) && (
                                                                <Typography variant={`body2`} display={`inline`}>
                                                                    {conversionUrlSettingState.interItemSeparatorApp}
                                                                </Typography>
                                                            )}
                                                            {conversionUrlSettingState.itemCodePrefixApp !== "" &&
                                                                conversionUrlSettingState.exampleItemCode2 !== "" &&
                                                                conversionUrlSettingState.exampleItemQTY2 !== "" && (
                                                                    <Typography variant={`body2`} display={`inline`}>
                                                                        {conversionUrlSettingState.itemCodePrefixApp}
                                                                    </Typography>
                                                                )}
                                                            {conversionUrlSettingState.exampleItemCode2 !== "" &&
                                                                conversionUrlSettingState.exampleItemQTY2 !== "" && (
                                                                    <>
                                                                        <Typography
                                                                            variant={`body2`}
                                                                            bgcolor={`#dbffe7`}
                                                                            display={`inline`}
                                                                        >
                                                                            {conversionUrlSettingState.exampleItemCode2}
                                                                        </Typography>
                                                                        <Typography variant={`body2`}
                                                                                    display={`inline`}>
                                                                            {conversionUrlSettingState.intraItemSeparatorApp}
                                                                        </Typography>
                                                                        <Typography variant={`body2`}
                                                                                    display={`inline`}>
                                                                            {conversionUrlSettingState.exampleItemQTY2}
                                                                        </Typography>
                                                                    </>
                                                                )}
                                                            {conversionUrlSettingState.itemOptionalParamValueApp !==
                                                                "" &&
                                                                conversionUrlSettingState.exampleItemCode2 !== "" &&
                                                                conversionUrlSettingState.exampleItemQTY2 !== "" && (
                                                                    <>
                                                                        <Typography variant={`body2`}
                                                                                    display={`inline`}>
                                                                            {conversionUrlSettingState.intraItemSeparatorApp}
                                                                        </Typography>
                                                                        <Typography variant={`body2`}
                                                                                    display={`inline`}>
                                                                            {conversionUrlSettingState.itemOptionalParamValueApp}
                                                                        </Typography>
                                                                    </>
                                                                )}
                                                            {/*{*/}
                                                            {/*    conversionUrlSettingState.exampleItemCode2 !== '' &&*/}
                                                            {/*    conversionUrlSettingState.exampleItemQTY2 !== '' &&*/}
                                                            {/*    conversionUrlSettingState.optionalVarName !== '' &&*/}
                                                            {/*    <Typography variant={`body2`} display={`inline`}>*/}
                                                            {/*        {conversionUrlSettingState.interItemSeparator}*/}
                                                            {/*    </Typography>*/}
                                                            {/*}*/}

                                                            {optionalVarStringsApp}
                                                        </Box>
                                                    }
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Box>
                                </Box>
                            </>
                        }
                        {
                            activeTabName === "checkoutTab" && settingForAccount &&
                            <Box
                                sx={{
                                    flex: `1 1`,
                                    overflow: `auto`,
                                }}
                            >
                                <AccountCheckoutSettingForPopup
                                    conversionUrlSettingState={conversionUrlSettingState}
                                    handleChange={handleChange}
                                    checkValidValue={checkValidValue}
                                    onChangePhoneNumber={onChangePhoneNumber}
                                    handleChangeRadioBtn={handleChangeRadioBtn}
                                />
                            </Box>
                        }
                        <Box px={`20px`} py={`10px`}>
                            {/*save btn*/}
                            <Box>
                                <Grid
                                    container
                                    alignItems={`center`}
                                    justifyContent={`flex-end`}
                                >
                                    <Grid item xs={2}>
                                        <Box sx={{display: `flex`, alignItems: `center`}}>
                                            <Button
                                                className={classes.buttonConversionUrlSettings}
                                                disabled={checkAllRequiredFields()}
                                                onClick={checkerBeforeSaveSettingsCheckoutUrl}
                                            >
                                                <Typography color={`#ffffff`}>Save</Typography>
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Modal>
            {isDomainChanged && (
                <SimpleConfirmPopup
                    onClose={changeDomain}
                    contentText={`Confirm Domain URL Change?`}
                    onConfirm={saveSettingUrl}
                    buttonText={[`Confirm`]}
                />
            )}
            {isChangesNotSaved && (
                <SimpleConfirmPopup
                    onClose={changeStatusForNotSavedChanges}
                    contentText={`Save edits before exit?`}
                    onConfirm={checkerBeforeSaveSettingsCheckoutUrl}
                    buttonText={[`Save`, `Don't Save`, `Cancel`]}
                    onCloseWithoutSaving={onCloseWithoutSaving}
                />
            )}
        </>
    );
};
